// useSubscriptionFeatures.js
// Custom hook to check subscription tier features and control UI access

import { useMemo } from 'react';
import { getTierById } from '../config/subscriptionTiers'; // Adjust path as needed

/**
 * Hook to check if user has access to specific features based on subscription tier
 * @param {string} userSubscriptionTier - User's current subscription tier (basic/standard/premium)
 * @returns {object} Object with feature access checks and tier info
 */
export const useSubscriptionFeatures = (userSubscriptionTier) => {
  const tierConfig = useMemo(() => {
    return getTierById(userSubscriptionTier || 'basic');
  }, [userSubscriptionTier]);

  return {
    // Tier info
    tierName: tierConfig?.name || 'Basic',
    tierId: tierConfig?.id || 'basic',
    
    // Feature access checks (UI visibility)
    canViewChangelog: tierConfig?.features?.changeLog || false,
    canViewWeatherAlerts: tierConfig?.features?.weatherAlerts || false,
    canViewReports: tierConfig?.features?.reports !== 'none',
    canPrintReports: tierConfig?.features?.reports === 'advanced-printable',
    
    // Feature usage checks (create/use functionality)
    canCreateRecurringAllocations: tierConfig?.features?.recurringAllocations || false,
    canUseFacilitySharing: tierConfig?.features?.facilitySharing || false,
    canCreateShareLinks: tierConfig?.features?.shareLink || false,
    canImportAllocations: tierConfig?.features?.importAllocations || false,
    canAddSponsorBranding: tierConfig?.features?.shareLinkSponsors || false,
    canUseSmartFixtureTool: tierConfig?.features?.smartFixtureTool || false,
    
    // Advance booking limit (in days)
    advanceBookingLimit: tierConfig?.id === 'basic' ? 31 : 365, // Basic: 31 days, Standard/Premium: 12 months
    
    // Limits
    pitchLimit: tierConfig?.features?.pitches || 2,
    teamLimit: tierConfig?.features?.teams || 10,
    userLimit: tierConfig?.features?.users || 1,
    
    // Full tier config
    tier: tierConfig,
  };
};

export default useSubscriptionFeatures;
